import { SbBlokData, storyblokEditable } from "@storyblok/react"
import { IoCheckmarkCircleSharp } from "react-icons/io5"
import { Anchor } from "../../app/components/anchor"

export interface IDriverFitItem extends SbBlokData {
  title: string
  description?: string
}

export interface IDriverFitChecklist extends SbBlokData {
  badge?: string
  heading: string
  intro?: string
  items?: IDriverFitItem[]
  prompt?: string
  buttonLabel?: string
  path?: string
  externalPath?: string
  footnote?: string
}

// Driver signup used by every CTA on /new-drivers. useCTARoute is not a
// fallback here: its default is the shipper listing flow.
const DEFAULT_PATH = "register"

function getCtaHref({ path, externalPath }: IDriverFitChecklist) {
  if (externalPath) {
    return externalPath
  }

  const route = (path || DEFAULT_PATH).replace(/^\/+/, "")

  return `/${route}`
}

export default function DriverFitChecklist({
  blok,
}: {
  blok: IDriverFitChecklist
}) {
  const { badge, heading, intro, prompt, buttonLabel, footnote } = blok
  const items = blok.items ?? []

  return (
    // Sits directly under the /new-drivers hero: no top padding, and -mt-20
    // cancels the hero's mb-20 so the checklist reads as the next step. Below md
    // -mt-28 also takes 32px of the hero's empty bottom padding. From lg the
    // hero's stacked images can reach its bottom edge, so 40px stays. This is
    // page-padding spelled out, since its unlayered copy overrides pt-0.
    <section
      className="-mt-28 md:-mt-20 lg:-mt-10 mx-auto w-full max-w-[1300px] px-4 pb-12 pt-0 sm:px-12 sm:pb-16 md:px-24 md:pb-20 lg:px-32 lg:pb-24 xl:px-36 xl:pb-28 2xl:px-44"
      {...storyblokEditable(blok)}
    >
      <div className="mx-auto max-w-[62rem] overflow-hidden rounded-2xl border border-blue24 bg-white shadow-lg">
        <div className="flex flex-col items-center bg-gradient-to-r from-[#0f1d3d] to-[#12389a] px-6 py-8 text-center md:px-12 md:py-6">
          {badge && (
            <span className="mb-4 rounded-md bg-white/20 px-2 py-1 text-xs font-bold uppercase tracking-wide text-white">
              {badge}
            </span>
          )}
          <h2 className="mb-0 font-montserrat text-2xl font-bold text-white md:text-3xl">
            {heading}
          </h2>
          {intro && (
            <p className="mb-0 mt-3 max-w-[38rem] whitespace-pre-line text-sm text-blue24 md:text-base">
              {intro}
            </p>
          )}
        </div>
        <div className="flex flex-col px-4 py-6 md:px-7 md:py-7">
          {items.length > 0 && (
            <>
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {items.map((item) => (
                  <li
                    key={item._uid}
                    className="flex items-start gap-3 rounded-lg border border-blue23 bg-[#f7f9fc] px-4 py-4"
                    {...storyblokEditable(item)}
                  >
                    <IoCheckmarkCircleSharp
                      aria-hidden="true"
                      className="mt-0.5 h-6 w-6 min-w-[1.5rem] fill-[#0f9d6b]"
                    />
                    <div className="flex flex-col">
                      <p className="mb-0 font-montserrat text-base font-bold text-blue5">
                        {item.title}
                      </p>
                      {item.description && (
                        <p className="mb-0 mt-1 whitespace-pre-line text-sm text-blue22">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <hr className="my-7 border-blue23" />
            </>
          )}
          <div className="flex flex-col items-center text-center">
            {prompt && (
              <p className="mb-4 font-montserrat text-base font-bold text-blue5">
                {prompt}
              </p>
            )}
            {buttonLabel && (
              <Anchor
                className="w-full rounded-lg bg-blue7 px-6 py-4 font-montserrat text-base font-bold md:px-10 md:text-lg text-white no-underline shadow-lg transition-colors hover:bg-blue15 hover:text-white hover:!no-underline md:w-auto"
                href={getCtaHref(blok)}
              >
                {buttonLabel}
              </Anchor>
            )}
            {footnote && (
              <p className="mb-0 mt-4 text-xs text-blue22">{footnote}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
